import React, { useState, useEffect, useCallback, useRef } from 'react';
import { timeAgo, fmtFull as formatDate } from '../../utils/dateUtils';
import { Mail, Send, Inbox, FileText, RefreshCw, ArrowLeft, Paperclip, X, Plus, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeFetch = async (url, opts = {}) => {
  const res = await fetch(url, { credentials: 'include', ...opts });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export default function AdminEmailView() {
  const [subTab, setSubTab] = useState('inbox');
  const [inboxEmails, setInboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch(`${API}/admin/email/inbox`);
      setInboxEmails(data);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchSent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch(`${API}/admin/email/sent`);
      setSentEmails(data);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch(`${API}/admin/email/drafts`);
      setDrafts(data);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const data = await safeFetch(`${API}/admin/email/inbox/unread-count`);
      setUnreadCount(data.count);
    } catch {}
  }, []);

  useEffect(() => {
    if (subTab === 'inbox') fetchInbox();
    else if (subTab === 'sent') fetchSent();
    else if (subTab === 'drafts') fetchDrafts();
    fetchUnread();
  }, [subTab, fetchInbox, fetchSent, fetchDrafts, fetchUnread]);

  const refresh = () => {
    if (subTab === 'inbox') fetchInbox();
    else if (subTab === 'sent') fetchSent();
    else if (subTab === 'drafts') fetchDrafts();
    fetchUnread();
  };

  const selectInboxEmail = async (email) => {
    setSelected(email);
    if (!email.is_read) {
      try {
        await safeFetch(`${API}/admin/email/inbox/${email.id}/read`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_read: true }),
        });
        setInboxEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
        fetchUnread();
      } catch {}
    }
    try {
      const t = await safeFetch(`${API}/admin/email/inbox/${email.id}/thread`);
      setThread(t);
    } catch { setThread([]); }
  };

  const handleReply = (email) => {
    setReplyTo(email);
    setShowCompose(true);
    setSelected(null);
  };

  const handleSent = () => {
    setShowCompose(false);
    setEditingDraft(null);
    setReplyTo(null);
    setSubTab('sent');
    fetchSent();
  };

  const handleDraftSaved = () => {
    setShowCompose(false);
    setEditingDraft(null);
    setReplyTo(null);
    setSubTab('drafts');
    fetchDrafts();
  };

  const deleteDraft = async (id) => {
    try {
      await safeFetch(`${API}/admin/email/drafts/${id}`, { method: 'DELETE' });
      setDrafts(prev => prev.filter(d => d.id !== id));
    } catch {}
  };

  // ── Compose view ──
  if (showCompose) {
    return (
      <div data-testid="admin-email-view">
        <AdminCompose
          onSent={handleSent}
          onCancel={() => { setShowCompose(false); setEditingDraft(null); setReplyTo(null); }}
          onDraftSaved={handleDraftSaved}
          editingDraft={editingDraft}
          replyTo={replyTo}
        />
      </div>
    );
  }

  // ── Detail view ──
  if (selected) {
    return (
      <div data-testid="admin-email-view">
        {subTab === 'inbox' ? (
          <AdminInboxDetail
            email={selected}
            thread={thread}
            onBack={() => { setSelected(null); setThread([]); }}
            onReply={handleReply}
          />
        ) : (
          <AdminEmailDetail
            email={selected}
            onBack={() => setSelected(null)}
          />
        )}
      </div>
    );
  }

  // ── List view ──
  const tabs = [
    { key: 'inbox', label: 'Inbox', count: unreadCount },
    { key: 'sent', label: 'Sent' },
    { key: 'drafts', label: 'Drafts', count: drafts.length },
  ];

  return (
    <div data-testid="admin-email-view" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-600/20 flex items-center justify-center">
            <Mail size={18} className="text-red-400" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-base">Admin Mailbox</h2>
            <p className="text-zinc-500 text-xs">admin@ntxpipefence.com</p>
          </div>
        </div>
        <Button
          data-testid="admin-compose-btn"
          onClick={() => { setShowCompose(true); setEditingDraft(null); setReplyTo(null); }}
          className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
          size="sm"
        >
          <Plus size={14} /> Compose
        </Button>
      </div>

      {/* Tabs + Refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`admin-email-tab-${t.key}`}
            onClick={() => { setSubTab(t.key); setSelected(null); }}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors flex items-center gap-1.5 ${
              subTab === t.key ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            {t.label}
            {t.count > 0 && subTab !== t.key && (
              <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full font-bold">{t.count}</span>
            )}
          </button>
        ))}
        <button
          data-testid="admin-email-refresh-btn"
          onClick={refresh}
          className="p-1.5 rounded bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-zinc-500 text-sm text-center py-12">Loading...</div>
      ) : subTab === 'inbox' ? (
        inboxEmails.length === 0 ? (
          <EmptyState icon={Inbox} title="Inbox is empty" sub="Inbound emails to admin@ntxpipefence.com will appear here" />
        ) : (
          <div className="space-y-1" data-testid="admin-inbox-list">
            {inboxEmails.map(e => (
              <InboxRow key={e.id} email={e} onClick={() => selectInboxEmail(e)} />
            ))}
          </div>
        )
      ) : subTab === 'sent' ? (
        sentEmails.length === 0 ? (
          <EmptyState icon={Send} title="No sent emails" sub="Emails you send will appear here" />
        ) : (
          <div className="space-y-1" data-testid="admin-sent-list">
            {sentEmails.map(e => (
              <SentRow key={e.id} email={e} onClick={() => setSelected(e)} />
            ))}
          </div>
        )
      ) : (
        drafts.length === 0 ? (
          <EmptyState icon={FileText} title="No drafts" sub="Saved drafts will appear here" />
        ) : (
          <div className="space-y-1" data-testid="admin-drafts-list">
            {drafts.map(d => (
              <DraftRow key={d.id} draft={d}
                onOpen={() => { setEditingDraft(d); setShowCompose(true); }}
                onDelete={() => deleteDraft(d.id)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ icon: Icon, title, sub }) {
  return (
    <Card className="border-zinc-800 bg-zinc-900">
      <CardContent className="py-12 text-center">
        <Icon size={32} className="text-zinc-700 mx-auto mb-3" />
        <p className="text-zinc-400 text-sm font-medium">{title}</p>
        <p className="text-zinc-600 text-xs mt-1">{sub}</p>
      </CardContent>
    </Card>
  );
}

/* ── Inbox Row ── */
function InboxRow({ email, onClick }) {
  return (
    <div
      data-testid={`admin-inbox-email-${email.id}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors border ${
        email.is_read
          ? 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800/70'
          : 'bg-zinc-800/60 border-blue-800/40 hover:bg-zinc-800'
      }`}
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${email.is_read ? 'bg-transparent' : 'bg-blue-500'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className={`text-sm truncate ${email.is_read ? 'text-zinc-300' : 'text-white font-semibold'}`}>
            {email.from_name || email.from_email}
          </span>
          <span className="text-zinc-600 text-[10px] shrink-0">{timeAgo(email.received_at || email.created_at)}</span>
        </div>
        <p className={`text-xs truncate ${email.is_read ? 'text-zinc-500' : 'text-zinc-300'}`}>
          {email.subject || '(No Subject)'}
        </p>
        <p className="text-[11px] text-zinc-600 truncate mt-0.5">{(email.body_text || '').slice(0, 80)}</p>
      </div>
      {email.attachments?.length > 0 && <Paperclip size={12} className="text-zinc-600 shrink-0" />}
    </div>
  );
}

/* ── Sent Row ── */
function SentRow({ email, onClick }) {
  return (
    <div
      data-testid={`admin-sent-email-${email.id}`}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-colors bg-zinc-900 border border-zinc-800 hover:bg-zinc-800/70"
    >
      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
        email.status === 'sent' ? 'bg-emerald-900/40' : email.status === 'failed' ? 'bg-red-900/40' : 'bg-zinc-800'
      }`}>
        <Send size={10} className={email.status === 'sent' ? 'text-emerald-400' : email.status === 'failed' ? 'text-red-400' : 'text-zinc-500'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm text-zinc-300 truncate">{email.to_email}</span>
          <span className="text-zinc-600 text-[10px] shrink-0">{timeAgo(email.created_at)}</span>
        </div>
        <p className="text-xs text-zinc-500 truncate">{email.subject || '(no subject)'}</p>
      </div>
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${
        email.status === 'sent' ? 'bg-emerald-900/40 text-emerald-400' :
        email.status === 'failed' ? 'bg-red-900/40 text-red-400' :
        'bg-zinc-800 text-zinc-500'
      }`}>{email.status}</span>
    </div>
  );
}

/* ── Draft Row ── */
function DraftRow({ draft, onOpen, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800">
      <div className="w-6 h-6 rounded-full bg-amber-900/30 flex items-center justify-center shrink-0">
        <FileText size={10} className="text-amber-400" />
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onOpen}>
        <p className="text-sm text-zinc-300 truncate">{draft.to_email || 'No recipient'}</p>
        <p className="text-xs text-zinc-500 truncate">{draft.subject || '(no subject)'}</p>
      </div>
      <span className="text-zinc-600 text-[10px] shrink-0">{timeAgo(draft.updated_at || draft.created_at)}</span>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="text-zinc-600 hover:text-red-400 transition-colors p-1" title="Delete draft">
        <X size={12} />
      </button>
    </div>
  );
}

/* ── Inbox Email Detail ── */
function AdminInboxDetail({ email, thread, onBack, onReply }) {
  return (
    <div data-testid="admin-inbox-detail" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="admin-email-back-btn"
          className="text-zinc-400 border-zinc-700 hover:bg-zinc-800 hover:text-white gap-1.5">
          <ArrowLeft size={14} /> Back
        </Button>
        <Button size="sm" onClick={() => onReply(email)} data-testid="admin-email-reply-btn"
          className="bg-red-600 hover:bg-red-700 text-white gap-1.5">
          <Send size={12} /> Reply
        </Button>
      </div>

      <Card className="border-zinc-800 bg-zinc-900">
        <CardContent className="p-5 space-y-4">
          <h2 className="text-white font-bold text-lg">{email.subject || '(No Subject)'}</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
            <span><strong className="text-zinc-300">From:</strong> {email.from_name || ''} &lt;{email.from_email}&gt;</span>
            <span><strong className="text-zinc-300">To:</strong> {(email.to || []).join(', ')}</span>
            {email.cc?.length > 0 && <span><strong className="text-zinc-300">CC:</strong> {email.cc.join(', ')}</span>}
            <span>{formatDate(email.received_at || email.created_at)}</span>
          </div>

          {/* Attachments */}
          {email.attachments?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-zinc-400 text-xs font-semibold mb-2">Attachments ({email.attachments.length})</p>
              <div className="flex flex-wrap gap-2">
                {email.attachments.map((att, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-zinc-700 rounded text-zinc-300">
                    {att.filename || 'attachment'} <span className="text-zinc-500">({att.content_type?.split('/')[1] || 'file'})</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-zinc-800 pt-4">
            {email.body_html ? (
              <div className="text-sm text-zinc-300 leading-relaxed prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: email.body_html }} />
            ) : (
              <pre className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap font-sans">
                {email.body_text || '(No content)'}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Thread */}
      {thread.length > 1 && (
        <div className="space-y-2">
          <h3 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Thread ({thread.length} messages)</h3>
          {thread.filter(t => t.id !== email.id).map(t => (
            <Card key={t.id} className={`border-zinc-800 bg-zinc-900/60 ${t.direction === 'inbound' ? 'border-l-2 border-l-blue-600' : 'border-l-2 border-l-emerald-600'}`}>
              <CardContent className="p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-semibold text-zinc-300">
                    {t.direction === 'inbound' ? `From: ${t.from_email}` : `Sent to: ${t.to_email}`}
                  </span>
                  <span className="text-[10px] text-zinc-600">{timeAgo(t.created_at)}</span>
                </div>
                <p className="text-xs text-zinc-400">{t.subject || '(No Subject)'}</p>
                <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.body_text?.slice(0, 200)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Sent Email Detail ── */
function AdminEmailDetail({ email, onBack }) {
  return (
    <div data-testid="admin-email-detail" className="space-y-3">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="admin-email-back-btn"
        className="text-zinc-400 border-zinc-700 hover:bg-zinc-800 hover:text-white gap-1.5">
        <ArrowLeft size={14} /> Back
      </Button>
      <Card className="border-zinc-800 bg-zinc-900">
        <CardContent className="p-5 space-y-4">
          <h2 className="text-white font-bold text-lg">{email.subject || '(no subject)'}</h2>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
            <span><strong className="text-zinc-300">From:</strong> {email.from_name} &lt;{email.from_email}&gt;</span>
            <span><strong className="text-zinc-300">To:</strong> {email.to_email}</span>
            <span>{formatDate(email.created_at)}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
              email.status === 'sent' ? 'bg-emerald-900/40 text-emerald-400' : 'bg-red-900/40 text-red-400'
            }`}>{email.status}</span>
          </div>
          {email.attachments?.length > 0 && (
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <p className="text-zinc-400 text-xs font-semibold mb-2">Attachments ({email.attachments.length})</p>
              <div className="flex flex-wrap gap-2">
                {email.attachments.map((att, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-zinc-700 rounded text-zinc-300">
                    {att.original_filename || att.filename || 'file'}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="border-t border-zinc-800 pt-4">
            {email.body_html ? (
              <div className="text-sm text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: email.body_html }} />
            ) : <p className="text-sm text-zinc-500">No content</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Compose ── */
function AdminCompose({ onSent, onCancel, onDraftSaved, editingDraft, replyTo }) {
  const [to, setTo] = useState(editingDraft?.to_email || replyTo?.from_email || '');
  const [cc, setCc] = useState(editingDraft?.cc?.join(', ') || '');
  const [subject, setSubject] = useState(editingDraft?.subject || (replyTo ? `Re: ${(replyTo.subject || '').replace(/^Re:\s*/i, '')}` : ''));
  const [body, setBody] = useState(editingDraft?.body_text || (replyTo ? `\n\n--- Original Message ---\nFrom: ${replyTo.from_email || ''}\nDate: ${replyTo.received_at || replyTo.created_at || ''}\nSubject: ${replyTo.subject || ''}\n\n${replyTo.body_text || ''}` : ''));
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftId, setDraftId] = useState(editingDraft?.id || null);
  const [attachments, setAttachments] = useState(editingDraft?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    setUploading(true);
    setError('');
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API}/admin/email/upload-attachment`, {
          method: 'POST', credentials: 'include', body: fd,
        });
        if (res.status === 401) {
          setError('Session expired. Please log in again.');
          setUploading(false);
          return;
        }
        if (res.ok) {
          const data = await res.json();
          setAttachments(prev => [...prev, data]);
        } else {
          setError(`Failed to upload ${file.name}. Please try again.`);
        }
      } catch (e) {
        setError(`Upload error: ${e.message}`);
      }
    }
    setUploading(false);
  };

  const sendEmail = async () => {
    setError('');
    setSuccess('');
    if (!to) { setError('Please enter a recipient email address.'); return; }
    if (!subject) { setError('Please enter a subject line.'); return; }
    setSending(true);
    try {
      const bodyHtml = body.split('\n').map(l => l.trim() ? `<p>${l}</p>` : '<br/>').join('');
      const res = await fetch(`${API}/admin/email/compose`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: to.trim(), subject, body_html: bodyHtml,
          cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          attachment_file_ids: attachments.map(a => a.id),
        }),
      });
      if (res.status === 401) {
        setError('Session expired. Please log in again.');
        setSending(false);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'failed') {
          setError(`Email failed: ${data.error || 'Unknown error'}. Provider: ${data.provider || '?'}`);
        } else if (data.status === 'test_logged') {
          setError('Email NOT sent — Resend is in TEST MODE. Check Admin > Email Diagnostics for setup.');
        } else {
          if (draftId) {
            await fetch(`${API}/admin/email/drafts/${draftId}`, { method: 'DELETE', credentials: 'include' });
          }
          onSent();
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        setError(errData.detail || `Send failed (${res.status}). Please try again.`);
      }
    } catch (e) {
      setError(`Network error: ${e.message}. Check your connection and try again.`);
    } finally { setSending(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const url = draftId ? `${API}/admin/email/drafts/${draftId}` : `${API}/admin/email/drafts`;
      const method = draftId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: to.trim(), subject, body_text: body,
          cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          attachment_file_ids: attachments.map(a => a.id),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!draftId) setDraftId(data.id);
        onDraftSaved();
      }
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-zinc-800 bg-zinc-900" data-testid="admin-compose-email">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm">{draftId ? 'Edit Draft' : replyTo ? 'Reply' : 'New Email'}</h3>
          <button onClick={onCancel} className="text-zinc-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="text-zinc-400 text-xs w-12 shrink-0">To:</label>
            <Input data-testid="admin-compose-to" value={to} onChange={e => setTo(e.target.value)}
              placeholder="recipient@email.com" className="bg-zinc-800 border-zinc-700 text-white text-sm" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-zinc-400 text-xs w-12 shrink-0">CC:</label>
            <Input data-testid="admin-compose-cc" value={cc} onChange={e => setCc(e.target.value)}
              placeholder="cc@email.com" className="bg-zinc-800 border-zinc-700 text-white text-sm" />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-zinc-400 text-xs w-12 shrink-0">Subject:</label>
            <Input data-testid="admin-compose-subject" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Email subject" className="bg-zinc-800 border-zinc-700 text-white text-sm" />
          </div>
        </div>

        {/* Attachments */}
        <div>
          <input ref={fileRef} type="file" multiple onChange={e => {
            if (e.target.files?.length) {
              const files = Array.from(e.target.files);
              const oversized = files.filter(f => f.size > 10 * 1024 * 1024);
              if (oversized.length) {
                setError(`File "${oversized[0].name}" exceeds 10MB limit. Compress or resize before attaching.`);
                e.target.value = '';
                return;
              }
              uploadFiles(files);
            }
            e.target.value = '';
          }} className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp,.heic,.gif,.mp4,.mov,.zip" />
          <button data-testid="admin-attach-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 text-zinc-400 rounded text-xs font-medium hover:text-white hover:bg-zinc-700 transition-colors">
            <Paperclip size={12} /> {uploading ? 'Uploading...' : 'Attach Files'}
          </button>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {attachments.map(att => (
                <span key={att.id} className="flex items-center gap-1 text-xs px-2 py-1 bg-zinc-800 rounded text-zinc-300">
                  {att.original_filename || att.filename}
                  {att.size && <span className="text-zinc-500 ml-0.5">({(att.size / 1024).toFixed(0)}KB)</span>}
                  <button onClick={() => setAttachments(prev => prev.filter(a => a.id !== att.id))} className="text-zinc-500 hover:text-red-400">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Error / Success Messages */}
        {error && (
          <div data-testid="admin-compose-error" className="bg-red-900/30 border border-red-800 rounded-lg px-4 py-2.5 text-red-300 text-xs flex items-start gap-2">
            <span className="shrink-0 mt-0.5">&#9888;</span>
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto shrink-0 text-red-500 hover:text-red-300"><X size={12} /></button>
          </div>
        )}
        {success && (
          <div data-testid="admin-compose-success" className="bg-emerald-900/30 border border-emerald-800 rounded-lg px-4 py-2.5 text-emerald-300 text-xs">
            {success}
          </div>
        )}

        {/* Body */}
        <textarea data-testid="admin-compose-body" value={body} onChange={e => setBody(e.target.value)}
          placeholder="Write your email..."
          className="w-full min-h-[200px] p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-200 placeholder:text-zinc-600 resize-y outline-none focus:border-zinc-500" />

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} className="text-zinc-400 border-zinc-700">Discard</Button>
            <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} data-testid="admin-save-draft-btn"
              className="text-amber-400 border-amber-800 hover:bg-amber-900/30">
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
          </div>
          <Button size="sm" onClick={sendEmail} disabled={sending || !to || !subject} data-testid="admin-send-btn"
            className="bg-red-600 hover:bg-red-700 text-white gap-1.5">
            <Send size={12} /> {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
